import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { getMediaPath } from 'app/functions';
import { TextHot } from 'components/common/TextHot';
import CardBackgroundImage from 'assets/HotYoter/Vector 5.svg';
import ArrowLeftIcon from 'assets/icons/arrow-left-black.svg';
import Number0Icon from 'assets/HotYoter/number0.svg';
import Number1Icon from 'assets/HotYoter/number1.svg';
import Number2Icon from 'assets/HotYoter/number2.svg';
import NumberGoldIcon from 'assets/HotYoter/numbergold.svg';
import {
    canFitCardsInline,
    CARD_SVG,
    getCardContentLayout,
    getCardDimensions,
    getCardTypographyScale,
    getInlineCardGapForViewport,
    getInlineCardWidth,
    getInlineMaxCardWidthForViewport,
    getMinInlineCardWidthForViewport,
    getResponsiveCardWidth,
    getUniformCardHeightOverride,
    INLINE_CARD_GAP,
    LG_INLINE_CARD_GAP,
    MAX_CARD_WIDTH,
    MD_INLINE_CARD_GAP,
    MD_LG_SCROLL_MAX_CARD_WIDTH,
    MOBILE_MAX_CARD_WIDTH,
    SCROLL_PEEK_VISIBLE_CARDS_MD,
    SCROLL_PEEK_VISIBLE_CARDS_MD_LG,
    SCROLL_PEEK_VISIBLE_CARDS_MOBILE,
    SCROLL_PEEK_VISIBLE_CARDS_TABLET,
    TABLET_SCROLL_MAX_CARD_WIDTH,
} from './cardLayout';

const getScrollSizingOptions = ({ isTabletUp, isMdUp, isXlUp, viewportWidth = 0 }) => {
    if (isMdUp && !isXlUp) {
        return {
            visibleCards: SCROLL_PEEK_VISIBLE_CARDS_MD_LG,
            gap: MD_INLINE_CARD_GAP,
            scrollMaxWidth: MD_LG_SCROLL_MAX_CARD_WIDTH,
        };
    }

    if (isXlUp) {
        return {
            visibleCards: SCROLL_PEEK_VISIBLE_CARDS_MD,
            gap: getInlineCardGapForViewport(viewportWidth),
            scrollMaxWidth: getInlineMaxCardWidthForViewport(viewportWidth, { isXlUp: true }),
        };
    }

    if (isTabletUp) {
        return {
            visibleCards: SCROLL_PEEK_VISIBLE_CARDS_TABLET,
            gap: INLINE_CARD_GAP,
            scrollMaxWidth: TABLET_SCROLL_MAX_CARD_WIDTH,
        };
    }

    return {
        visibleCards: SCROLL_PEEK_VISIBLE_CARDS_MOBILE,
        gap: INLINE_CARD_GAP,
        scrollMaxWidth: MOBILE_MAX_CARD_WIDTH,
    };
};

const NUMBER_BADGE_ICONS = {
    0: Number0Icon,
    1: Number1Icon,
    2: Number2Icon,
};

const TABLET_UP = '@media (min-width: 768px)';

const CarouselWrapper = styled('div', {
    shouldForwardProp: (prop) => prop !== 'hasSideArrows',
})(({ hasSideArrows }) => ({
    width: '100%',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    ...(hasSideArrows && {
        // Leave gutters so side arrows sit beside the cards, not over them.
        paddingLeft: 28,
        paddingRight: 28,
        boxSizing: 'border-box',
    }),
}));

const CarouselRoot = styled('div', {
    shouldForwardProp: (prop) => prop !== 'allowVerticalOverflow',
})(({ theme, allowVerticalOverflow }) => ({
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    overflow: allowVerticalOverflow ? 'visible' : 'hidden',
    direction: 'rtl',
    display: 'flex',
    flexDirection: 'column',
    [TABLET_UP]: {
        flex: 1,
        minWidth: 0,
        maxWidth: '100%',
    },
}));

const CarouselScroll = styled('div', {
    shouldForwardProp: (prop) => prop !== 'fitsInline'
        && prop !== 'cardRowHeight'
        && prop !== 'hasHeightOverride'
        && prop !== 'isDesktopInline'
        && prop !== 'isScrollMode'
        && prop !== 'inlineGap',
})(({ theme, fitsInline, cardRowHeight, hasHeightOverride, isDesktopInline, isScrollMode, inlineGap }) => ({
    display: 'flex',
    flexDirection: 'row',
    direction: isScrollMode ? 'rtl' : 'ltr',
    gap: theme.spacing(1),
    overflowX: 'auto',
    padding: `${theme.spacing(0.25)} 0 ${theme.spacing(0.5)}`,
    scrollSnapType: 'x mandatory',
    scrollSnapStop: 'normal',
    WebkitOverflowScrolling: 'touch',
    '&::-webkit-scrollbar': {
        display: 'none',
    },
    ...(cardRowHeight != null && {
        minHeight: cardRowHeight,
        ...(hasHeightOverride ? {} : { maxHeight: cardRowHeight }),
    }),
    ...(fitsInline && {
        overflow: 'visible',
        paddingRight: 0,
        paddingLeft: 0,
        paddingBottom: 0,
        // Space leftover width evenly across flex slots so ultra-wide
        // viewports don't pile empty space on one side.
        justifyContent: isDesktopInline ? 'space-between' : 'flex-start',
        width: '100%',
        ...(inlineGap != null ? { gap: `${inlineGap}px` } : null),
    }),
    [theme.breakpoints.up('sm')]: {
        gap: fitsInline && inlineGap != null ? `${inlineGap}px` : theme.spacing(1.25),
        paddingBottom: 0,
    },
    [theme.breakpoints.up('md')]: {
        gap: fitsInline && inlineGap != null ? `${inlineGap}px` : theme.spacing(1.5),
    },
    [theme.breakpoints.up('lg')]: {
        gap: fitsInline && inlineGap != null ? `${inlineGap}px` : theme.spacing(2),
    },
}));

const CarouselDots = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    direction: 'rtl',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(0.25),
    width: '100%',
}));

const CarouselDot = styled('button', {
    shouldForwardProp: (prop) => prop !== 'active',
})(({ active }) => ({
    width: 6,
    height: 6,
    padding: 0,
    border: 'none',
    borderRadius: '50%',
    cursor: 'pointer',
    flexShrink: 0,
    background: active ? '#000' : 'rgba(0, 0, 0, 0.25)',
    transition: 'background 0.2s ease, transform 0.2s ease',
    ...(active && {
        transform: 'scale(1.35)',
    }),
}));

const CarouselArrow = styled('button')(({ disabled }) => ({
    position: 'absolute',
    zIndex: 2,
    top: '50%',
    flexShrink: 0,
    width: 20,
    height: 20,
    padding: 0,
    border: 'none',
    backgroundColor: 'transparent',
    backgroundImage: `url(${ArrowLeftIcon})`,
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
    cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.25 : 1,
    pointerEvents: disabled ? 'none' : 'auto',
    transition: 'opacity 0.2s ease',
}));

const CardOuter = styled('div', {
    shouldForwardProp: (prop) => prop !== 'fitsInline' && prop !== 'layoutWidth',
})(({ theme, fitsInline, layoutWidth }) => ({
    flex: fitsInline ? '1 1 0' : '0 0 auto',
    minWidth: fitsInline ? 0 : undefined,
    // In scroll mode keep a fixed track size. In inline mode let the outer
    // flex slot grow so leftover width is shared; the inner card stays at
    // layoutWidth so text coordinates remain correct.
    maxWidth: fitsInline
        ? undefined
        : (layoutWidth != null ? layoutWidth : undefined),
    scrollSnapAlign: 'start',
    filter: `drop-shadow(0 ${theme.spacing(0.375)} ${theme.spacing(1.25)} rgba(46, 99, 238, 0.14))`,
    ...(fitsInline && {
        display: 'flex',
        justifyContent: 'center',
    }),
}));

const Card = styled('div')({
    position: 'relative',
    overflow: 'visible',
    direction: 'ltr',
});

const getCardSurfaceStyle = (layout) => (
    layout.isTallCard
        ? {
            borderBottomLeftRadius: `${Math.round((20 / CARD_SVG.width) * layout.width)}px`,
            borderBottomRightRadius: `${Math.round((20 / CARD_SVG.width) * layout.width)}px`,
        }
        : undefined
);

const CardBackground = styled('img')({
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    display: 'block',
    pointerEvents: 'none',
    objectFit: 'fill',
});

const CardBadge = styled('img')({
    position: 'absolute',
    zIndex: 2,
    display: 'block',
    pointerEvents: 'none',
});

const CardTitleContent = styled('div')({
    position: 'absolute',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 1,
    overflow: 'hidden',
    alignItems: 'stretch',
    textAlign: 'center',
    direction: 'rtl',
    justifyContent: 'center',
});

const BODY_CONTENT_MARGIN_TOP = 10;

const CardBodyContent = styled('div')({
    position: 'absolute',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 1,
    overflow: 'hidden',
    alignItems: 'stretch',
    direction: 'rtl',
    textAlign: 'right',
    justifyContent: 'flex-start',
});

const CardBodyInner = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    alignItems: 'stretch',
    flex: '1 1 auto',
    minHeight: 0,
    overflow: 'hidden',
});

const BASE_CARD_TITLE_FONT_SIZE = 19;
const BASE_CARD_BODY_BOLD_FONT_SIZE = 19;
const BASE_CARD_BODY_SUBTLE_FONT_SIZE = 19;
const BASE_CARD_BODY_BOLD_MOBILE_FONT_SIZE = 18;

const getCardTextSizes = (isMdUp) => ({
    title: 'l',
    bodyBold: isMdUp ? 'm' : 'l',
    bodySubtle: 'm',
});

const getScaledFontSize = (baseSize, scale) => (
    scale <= 1 ? undefined : Math.round(baseSize * scale)
);

const getTitleTextSx = (typographyScale = 1) => {
    const fontSize = getScaledFontSize(BASE_CARD_TITLE_FONT_SIZE, typographyScale);

    return {
        m: 0,
        width: '100%',
        textAlign: 'center',
        direction: 'rtl',
        unicodeBidi: 'plaintext',
        ...(fontSize != null ? { fontSize: `${fontSize}px` } : null),
    };
};

const getBodyTextSx = (typographyScale = 1, { isBold = false, isMdUp = false } = {}) => {
    const baseSize = isBold
        ? (isMdUp ? BASE_CARD_BODY_BOLD_FONT_SIZE : BASE_CARD_BODY_BOLD_MOBILE_FONT_SIZE)
        : BASE_CARD_BODY_SUBTLE_FONT_SIZE;
    const fontSize = getScaledFontSize(baseSize, typographyScale);

    return {
        m: 0,
        width: '100%',
        textAlign: 'right',
        direction: 'rtl',
        unicodeBidi: 'plaintext',
        wordBreak: 'break-word',
        ...(fontSize != null ? { fontSize: `${fontSize}px` } : null),
    };
};

const getClampStyle = (lines) => ({
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: lines,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
});

const getBadgeCount = (index) => index;

const getCarouselItems = (sections, reverseForScroll) => {
    const items = sections.map((section, index) => ({
        section,
        index,
        key: `${section.title}-${index}`,
    }));

    return reverseForScroll ? [...items].reverse() : items;
};

const scrollCarouselToGoldStart = (element) => {
    if (!element) {
        return;
    }

    element.scrollLeft = 0;

    requestAnimationFrame(() => {
        element.scrollLeft = 0;
    });
};

const getDomIndexForLogicalIndex = (logicalIndex, slideCount, isScrollMode) => (
    isScrollMode ? slideCount - 1 - logicalIndex : logicalIndex
);

const getLogicalIndexFromDomIndex = (domIndex, slideCount, isScrollMode) => (
    isScrollMode ? slideCount - 1 - domIndex : domIndex
);

const scrollToLogicalSlide = (element, logicalIndex, slideCount, isScrollMode) => {
    if (!element || slideCount <= 1) {
        return;
    }

    const domIndex = getDomIndexForLogicalIndex(logicalIndex, slideCount, isScrollMode);
    const target = element.children[domIndex];

    if (target) {
        target.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'start',
        });
    }
};

const findMostVisibleDomIndex = (scrollEl) => {
    const rootRect = scrollEl.getBoundingClientRect();
    let bestDom = 0;
    let bestOverlap = 0;

    Array.from(scrollEl.children).forEach((child, domIndex) => {
        const rect = child.getBoundingClientRect();
        const overlap = Math.max(
            0,
            Math.min(rect.right, rootRect.right) - Math.max(rect.left, rootRect.left),
        );

        if (overlap > bestOverlap) {
            bestOverlap = overlap;
            bestDom = domIndex;
        }
    });

    return bestDom;
};

const getActiveSlideIndexFromScroll = (scrollEl, slideCount, isScrollMode) => {
    const domIndex = findMostVisibleDomIndex(scrollEl);

    return getLogicalIndexFromDomIndex(domIndex, slideCount, isScrollMode);
};

const getAbsoluteBoxStyle = ({ top, bottom, left, right, gap }) => ({
    top: `${top}px`,
    bottom: `${bottom}px`,
    left: `${left}px`,
    right: `${right}px`,
    ...(gap != null ? { gap: `${gap}px` } : {}),
});

const measureNaturalSubtitleHeight = (subtitleWrap, textWidth) => {
    const subtitleEl = subtitleWrap?.firstElementChild;

    if (!subtitleEl) {
        return 0;
    }

    const previousDisplay = subtitleEl.style.display;
    const previousLineClamp = subtitleEl.style.webkitLineClamp;
    const previousOverflow = subtitleEl.style.overflow;
    const previousWidth = subtitleEl.style.width;

    subtitleEl.style.display = 'block';
    subtitleEl.style.webkitLineClamp = 'unset';
    subtitleEl.style.overflow = 'visible';
    if (textWidth > 0) {
        subtitleEl.style.width = `${textWidth}px`;
    }

    const height = subtitleEl.scrollHeight;

    subtitleEl.style.display = previousDisplay;
    subtitleEl.style.webkitLineClamp = previousLineClamp;
    subtitleEl.style.overflow = previousOverflow;
    subtitleEl.style.width = previousWidth;

    return height;
};

const measureNaturalBodyContentHeight = ({
    subtitleBoldRef,
    subtitleWrapRef,
    bodyGap,
    section,
    textWidth,
}) => {
    let total = 0;

    if (section.subtitleBold && subtitleBoldRef.current) {
        total += subtitleBoldRef.current.offsetHeight;
    }

    if (section.subtitle && subtitleWrapRef.current) {
        if (total > 0) {
            total += bodyGap;
        }

        total += measureNaturalSubtitleHeight(subtitleWrapRef.current, textWidth);
    }

    return total;
};

const getSubtitleLineHeight = (subtitleEl) => {
    const computed = window.getComputedStyle(subtitleEl);
    let lineHeight = parseFloat(computed.lineHeight);

    if (!Number.isFinite(lineHeight) || lineHeight <= 0) {
        lineHeight = parseFloat(computed.fontSize) * 1.2;
    }

    return Number.isFinite(lineHeight) && lineHeight > 0 ? lineHeight : null;
};

const getAvailableSubtitleHeight = ({
    inner,
    subtitleBoldRef,
    bodyGap,
}) => {
    let available = inner.clientHeight;

    if (subtitleBoldRef.current) {
        available -= subtitleBoldRef.current.offsetHeight + bodyGap;
    }

    return Math.max(available, 0);
};

const HotYoterHowToGetCard = ({
    section,
    index,
    total,
    cardWidth,
    cardMaxWidth = MAX_CARD_WIDTH,
    cardHeightOverride = null,
    fitsInline = false,
    isMdUp = false,
    hasResolvedContentHeight = false,
    onBodyContentHeightReport,
    testID,
}) => {
    const layout = getCardContentLayout(cardWidth, cardMaxWidth, cardHeightOverride);
    const typographyScale = getCardTypographyScale(layout.width);
    const bodyContentMarginTop = Math.round(BODY_CONTENT_MARGIN_TOP * typographyScale);
    const textSizes = getCardTextSizes(isMdUp);
    const badgeCount = getBadgeCount(index);
    const isGoldBadge = section?.badgeType === 'gold'
        || (index === total - 1 && total >= 4);

    const bodyInnerRef = useRef(null);
    const subtitleBoldRef = useRef(null);
    const subtitleWrapRef = useRef(null);
    const [preMeasureClamp, setPreMeasureClamp] = useState(1);
    const [overflowFallback, setOverflowFallback] = useState('none');

    useLayoutEffect(() => {
        const inner = bodyInnerRef.current;
        const subtitleWrap = subtitleWrapRef.current;

        if (!hasResolvedContentHeight) {
            setOverflowFallback('none');

            if (inner && subtitleWrap && section.subtitle) {
                const available = getAvailableSubtitleHeight({
                    inner,
                    subtitleBoldRef,
                    bodyGap: layout.bodyGap,
                });
                const subtitleEl = subtitleWrap.firstElementChild || subtitleWrap;
                const lineHeight = getSubtitleLineHeight(subtitleEl);

                if (lineHeight) {
                    const lines = Math.max(1, Math.floor(available / lineHeight));
                    setPreMeasureClamp((current) => (current === lines ? current : lines));
                }
            }

            const naturalHeight = measureNaturalBodyContentHeight({
                subtitleBoldRef,
                subtitleWrapRef,
                bodyGap: layout.bodyGap,
                section,
                textWidth: layout.bodyTextMaxWidth,
            });

            onBodyContentHeightReport?.(index, naturalHeight);
            return undefined;
        }

        if (!inner) {
            return undefined;
        }

        const applyOverflowFallback = () => {
            if (inner.scrollHeight <= inner.clientHeight + 1) {
                setOverflowFallback((current) => (current === 'none' ? current : 'none'));
                return;
            }

            if (subtitleWrap && section.subtitle) {
                const available = getAvailableSubtitleHeight({
                    inner,
                    subtitleBoldRef,
                    bodyGap: layout.bodyGap,
                });
                const subtitleEl = subtitleWrap.firstElementChild || subtitleWrap;
                const lineHeight = getSubtitleLineHeight(subtitleEl);

                if (lineHeight && available > 0) {
                    const lines = Math.max(1, Math.floor(available / lineHeight));
                    setPreMeasureClamp((current) => (current === lines ? current : lines));
                    setOverflowFallback((current) => (current === 'clamp' ? current : 'clamp'));
                    return;
                }
            }

            setOverflowFallback((current) => (current === 'scroll' ? current : 'scroll'));
        };

        applyOverflowFallback();

        const observer = typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(applyOverflowFallback)
            : null;

        if (observer) {
            observer.observe(inner);
        }

        return () => {
            observer?.disconnect();
        };
    }, [
        section.subtitle,
        section.subtitleBold,
        layout.bodyGap,
        layout.bodyTextMaxWidth,
        layout.width,
        layout.height,
        isMdUp,
        hasResolvedContentHeight,
        cardHeightOverride,
        onBodyContentHeightReport,
        index,
        typographyScale,
    ]);

    useLayoutEffect(() => {
        if (!hasResolvedContentHeight || !bodyInnerRef.current) {
            return undefined;
        }

        const inner = bodyInnerRef.current;

        if (inner.scrollHeight > inner.clientHeight + 1) {
            onBodyContentHeightReport?.(index, inner.scrollHeight, 'refine');
        }

        return undefined;
    }, [
        hasResolvedContentHeight,
        cardHeightOverride,
        layout.width,
        layout.height,
        section.subtitle,
        section.subtitleBold,
        onBodyContentHeightReport,
        index,
        typographyScale,
    ]);

    const subtitleStyle = !hasResolvedContentHeight || overflowFallback === 'clamp'
        ? getClampStyle(preMeasureClamp)
        : undefined;

    const bodyInnerOverflow = overflowFallback === 'scroll' ? 'auto' : 'hidden';

    return (
        <CardOuter
            className="hot-yoter-how-to-get-carousel__card-outer"
            fitsInline={fitsInline}
            layoutWidth={layout.width}
            testId={`${testID}-card-${index}`}
            style={fitsInline ? undefined : { width: layout.width }}
        >
            <Card
                className="hot-yoter-how-to-get-carousel__card"
                style={{
                    width: layout.width,
                    maxWidth: layout.width,
                    height: layout.height,
                    ...getCardSurfaceStyle(layout),
                }}
            >
                <CardBackground
                    className="hot-yoter-how-to-get-carousel__card-background"
                    src={getMediaPath(CardBackgroundImage)}
                    alt=""
                    style={layout.isTallCard ? { height: layout.referenceHeight } : undefined}
                />

                {isGoldBadge ? (
                    <CardBadge
                        className="hot-yoter-how-to-get-carousel__badge"
                        src={getMediaPath(NumberGoldIcon)}
                        alt=""
                        testId={`${testID}-badge-${index}`}
                        style={{
                            left: layout.badge.goldLeft,
                            top: layout.badge.goldTop,
                            width: layout.badgeCircleSize,
                            height: layout.badgeCircleSize,
                        }}
                    />
                ) : (
                    <CardBadge
                        className="hot-yoter-how-to-get-carousel__badge"
                        src={getMediaPath(NUMBER_BADGE_ICONS[badgeCount] ?? Number0Icon)}
                        alt=""
                        testId={`${testID}-badge-${index}`}
                        style={{
                            left: layout.badge.left,
                            top: layout.badge.top,
                            width: layout.badge.imgWidth,
                            height: layout.badge.imgHeight,
                        }}
                    />
                )}

                <CardTitleContent
                    className="hot-yoter-how-to-get-carousel__title-content"
                    dir="rtl"
                    testId={`${testID}-card-title-content-${index}`}
                    style={getAbsoluteBoxStyle(layout.title)}
                >
                    <TextHot
                        size={textSizes.title}
                        bold="bold"
                        color="primary"
                        component="p"
                        sx={getTitleTextSx(typographyScale)}
                        testID={`${testID}-card-title-${index}`}
                    >
                        {section.title}
                    </TextHot>
                </CardTitleContent>

                <CardBodyContent
                    className="hot-yoter-how-to-get-carousel__body-content"
                    dir="rtl"
                    testId={`${testID}-card-body-content-${index}`}
                    style={{
                        ...getAbsoluteBoxStyle(layout.body),
                        marginTop: `${bodyContentMarginTop}px`,
                    }}
                >
                    <CardBodyInner
                        ref={bodyInnerRef}
                        className="hot-yoter-how-to-get-carousel__body-inner"
                        style={{
                            gap: `${layout.bodyGap}px`,
                            overflowY: bodyInnerOverflow,
                            WebkitOverflowScrolling: 'touch',
                        }}
                    >
                        {section.subtitleBold ? (
                            <div
                                ref={subtitleBoldRef}
                                className="hot-yoter-how-to-get-carousel__body-bold-wrap"
                                style={{ flexShrink: 0, width: '100%' }}
                            >
                                <TextHot
                                    size={textSizes.bodyBold}
                                    bold="bold"
                                    color="primary"
                                    component="p"
                                    className="hot-yoter-how-to-get-carousel__body-text hot-yoter-how-to-get-carousel__body-text_bold"
                                    sx={getBodyTextSx(typographyScale, { isBold: true, isMdUp })}
                                    testID={`${testID}-card-bold-${index}`}
                                >
                                    {section.subtitleBold}
                                </TextHot>
                            </div>
                        ) : null}
                        {section.subtitle ? (
                            <div
                                ref={subtitleWrapRef}
                                className="hot-yoter-how-to-get-carousel__body-subtitle-wrap"
                                style={{
                                    flex: '1 1 auto',
                                    minHeight: 0,
                                    width: '100%',
                                    overflow: overflowFallback === 'scroll' ? 'visible' : 'hidden',
                                }}
                            >
                                <TextHot
                                    size={textSizes.bodySubtle}
                                    color="subtle"
                                    component="p"
                                    className="hot-yoter-how-to-get-carousel__body-text hot-yoter-how-to-get-carousel__body-text_subtle"
                                    sx={getBodyTextSx(typographyScale, { isMdUp })}
                                    style={subtitleStyle}
                                    testID={`${testID}-card-subtitle-${index}`}
                                >
                                    {section.subtitle}
                                </TextHot>
                            </div>
                        ) : null}
                    </CardBodyInner>
                </CardBodyContent>
            </Card>
        </CardOuter>
    );
};

export const HotYoterHowToGetCarousel = ({
    sections = [],
    onCardRowHeightChange,
    testID = 'hot-yoter-how-to-get-carousel',
}) => {
    const theme = useTheme();
    const wrapperRef = useRef(null);
    const scrollRef = useRef(null);
    const measureFrameRef = useRef(null);
    const [cardWidth, setCardWidth] = useState(180);
    const [fitsInline, setFitsInline] = useState(false);
    const [inlineGap, setInlineGap] = useState(LG_INLINE_CARD_GAP);
    const [viewportWidth, setViewportWidth] = useState(() => (
        typeof window !== 'undefined' ? window.innerWidth : 0
    ));
    const [activeSlideIndex, setActiveSlideIndex] = useState(() => (
        sections.length > 0 ? sections.length - 1 : 0
    ));
    const deviceState = useSelector((store) => store.state.deviceState);
    const isMobile = deviceState === 'mobile';
    const isTabletUp = useMediaQuery('(min-width:768px)');
    const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
    const isXlUp = useMediaQuery(theme.breakpoints.up('xl'));
    const isScrollMode = !fitsInline;
    const inlineMaxWidth = getInlineMaxCardWidthForViewport(viewportWidth, { isXlUp });
    const scrollSizing = getScrollSizingOptions({
        isTabletUp,
        isMdUp,
        isXlUp,
        viewportWidth,
    });
    const cardMaxWidth = isScrollMode ? scrollSizing.scrollMaxWidth : inlineMaxWidth;
    const bodyContentMarginTop = Math.round(
        BODY_CONTENT_MARGIN_TOP * getCardTypographyScale(cardWidth),
    );
    const bodyContentHeightsRef = useRef({});
    const contentHeightResolvedRef = useRef(false);
    const contentHeightRefineRef = useRef(false);
    const [cardHeightOverride, setCardHeightOverride] = useState(null);
    const [hasResolvedContentHeight, setHasResolvedContentHeight] = useState(false);
    const cardRowHeight = getCardDimensions(
        cardWidth,
        cardMaxWidth,
        cardHeightOverride,
    ).height;

    const handleBodyContentHeightReport = useCallback((index, height, mode = 'initial') => {
        if (mode === 'refine') {
            if (contentHeightRefineRef.current || !Number.isFinite(height) || height <= 0) {
                return;
            }

            bodyContentHeightsRef.current[index] = Math.max(
                bodyContentHeightsRef.current[index] || 0,
                height,
            );

            const refinedOverride = getUniformCardHeightOverride(
                sections.map((_, sectionIndex) => bodyContentHeightsRef.current[sectionIndex]),
                cardWidth,
                cardMaxWidth,
                { bodyTopMargin: bodyContentMarginTop },
            );

            if (refinedOverride == null) {
                return;
            }

            contentHeightRefineRef.current = true;

            setCardHeightOverride((currentOverride) => (
                currentOverride == null || refinedOverride > currentOverride
                    ? refinedOverride
                    : currentOverride
            ));
            return;
        }

        if (contentHeightResolvedRef.current) {
            return;
        }

        bodyContentHeightsRef.current[index] = height;

        const reportedHeights = sections.map((_, sectionIndex) => (
            bodyContentHeightsRef.current[sectionIndex]
        ));

        if (reportedHeights.some((reportedHeight) => reportedHeight == null)) {
            return;
        }

        contentHeightResolvedRef.current = true;

        setCardHeightOverride(getUniformCardHeightOverride(
            reportedHeights,
            cardWidth,
            cardMaxWidth,
            { bodyTopMargin: bodyContentMarginTop },
        ));
        setHasResolvedContentHeight(true);
    }, [sections, cardWidth, cardMaxWidth, bodyContentMarginTop]);
    const isDesktopInline = fitsInline && isTabletUp && !isMobile;
    const showScrollControls = isScrollMode && sections.length > 1;
    // Mobile keeps dots; tablet+ uses prev/next arrows instead.
    const showScrollDots = showScrollControls && !isTabletUp;
    const showScrollArrows = showScrollControls && isTabletUp;
    const carouselItems = useMemo(
        () => getCarouselItems(sections, isScrollMode),
        [sections, isScrollMode],
    );
    const dotItems = useMemo(
        () => [...sections].reverse().map((section, reverseIndex) => ({
            section,
            index: sections.length - 1 - reverseIndex,
        })),
        [sections],
    );
    // Scroll mode reverses DOM order so gold/start sits at scrollLeft = 0
    // (highest logical index). Start arrow moves toward that end; end arrow
    // toward lower indices.
    const canScrollTowardStart = activeSlideIndex < sections.length - 1;
    const canScrollTowardEnd = activeSlideIndex > 0;

    const handleDotClick = useCallback((index) => {
        scrollToLogicalSlide(scrollRef.current, index, sections.length, isScrollMode);
        setActiveSlideIndex(index);
    }, [sections.length, isScrollMode]);

    const handleScrollTowardStart = useCallback(() => {
        if (!canScrollTowardStart) {
            return;
        }

        handleDotClick(activeSlideIndex + 1);
    }, [activeSlideIndex, canScrollTowardStart, handleDotClick]);

    const handleScrollTowardEnd = useCallback(() => {
        if (!canScrollTowardEnd) {
            return;
        }

        handleDotClick(activeSlideIndex - 1);
    }, [activeSlideIndex, canScrollTowardEnd, handleDotClick]);

    const syncActiveSlideFromScroll = useCallback(() => {
        const element = scrollRef.current;

        if (!element || !isScrollMode) {
            return;
        }

        setActiveSlideIndex(getActiveSlideIndexFromScroll(
            element,
            sections.length,
            isScrollMode,
        ));
    }, [isScrollMode, sections.length]);

    useLayoutEffect(() => {
        if (!onCardRowHeightChange) {
            return undefined;
        }

        onCardRowHeightChange(isTabletUp && !isMobile ? cardRowHeight : null);

        return undefined;
    }, [cardRowHeight, isTabletUp, isMobile, onCardRowHeightChange]);

    useLayoutEffect(() => {
        const measure = () => {
            if (!wrapperRef.current) {
                return;
            }

            const nextViewportWidth = window.innerWidth;
            const containerWidth = wrapperRef.current.clientWidth;
            const nextInlineGap = getInlineCardGapForViewport(nextViewportWidth);
            const nextInlineMaxWidth = getInlineMaxCardWidthForViewport(nextViewportWidth, {
                isXlUp,
            });
            const nextMinInlineWidth = getMinInlineCardWidthForViewport(nextViewportWidth);
            const scrollOptions = getScrollSizingOptions({
                isTabletUp,
                isMdUp,
                isXlUp,
                viewportWidth: nextViewportWidth,
            });
            const inlineCardWidth = getInlineCardWidth(
                containerWidth,
                sections.length,
                nextInlineGap,
                nextInlineMaxWidth,
            );
            const shouldFitInline = isXlUp
                && sections.length > 0
                && sections.length <= 4
                && inlineCardWidth >= nextMinInlineWidth
                && canFitCardsInline(
                    containerWidth,
                    sections.length,
                    nextInlineGap,
                    nextInlineMaxWidth,
                );

            setViewportWidth((current) => (
                current === nextViewportWidth ? current : nextViewportWidth
            ));
            setInlineGap((current) => (current === nextInlineGap ? current : nextInlineGap));

            if (shouldFitInline && containerWidth > 0) {
                const nextCardWidth = inlineCardWidth;

                setFitsInline((current) => (current === true ? current : true));
                setCardWidth((current) => {
                    if (current === nextCardWidth) {
                        return current;
                    }

                    bodyContentHeightsRef.current = {};
                    contentHeightResolvedRef.current = false;
                    contentHeightRefineRef.current = false;
                    setCardHeightOverride(null);
                    setHasResolvedContentHeight(false);
                    return nextCardWidth;
                });
                return;
            }

            const nextCardWidth = getResponsiveCardWidth(
                containerWidth,
                sections.length,
                false,
                {
                    forceScroll: true,
                    ...scrollOptions,
                },
            );

            setFitsInline((current) => (current === false ? current : false));
            setCardWidth((current) => {
                if (current === nextCardWidth) {
                    return current;
                }

                bodyContentHeightsRef.current = {};
                contentHeightResolvedRef.current = false;
                contentHeightRefineRef.current = false;
                setCardHeightOverride(null);
                setHasResolvedContentHeight(false);
                return nextCardWidth;
            });
        };

        const scheduleMeasure = () => {
            if (measureFrameRef.current !== null) {
                return;
            }

            measureFrameRef.current = requestAnimationFrame(() => {
                measureFrameRef.current = null;
                measure();
            });
        };

        measure();

        const element = wrapperRef.current;
        const resizeObserver = typeof ResizeObserver !== 'undefined' && element
            ? new ResizeObserver(scheduleMeasure)
            : null;

        if (resizeObserver && element) {
            resizeObserver.observe(element);
        }

        window.addEventListener('resize', scheduleMeasure);

        return () => {
            if (measureFrameRef.current !== null) {
                cancelAnimationFrame(measureFrameRef.current);
                measureFrameRef.current = null;
            }

            resizeObserver?.disconnect();
            window.removeEventListener('resize', scheduleMeasure);
        };
    }, [sections.length, isTabletUp, isMdUp, isXlUp]);

    useLayoutEffect(() => {
        if (!scrollRef.current || !sections.length || !isScrollMode) {
            return undefined;
        }

        const element = scrollRef.current;

        const alignToGold = () => {
            scrollCarouselToGoldStart(element);
            setActiveSlideIndex(sections.length - 1);
            requestAnimationFrame(syncActiveSlideFromScroll);
        };

        alignToGold();

        const frameId = requestAnimationFrame(() => {
            alignToGold();
            requestAnimationFrame(alignToGold);
        });
        const timeoutId = window.setTimeout(alignToGold, 50);

        return () => {
            cancelAnimationFrame(frameId);
            window.clearTimeout(timeoutId);
        };
    }, [sections.length, cardWidth, isScrollMode, syncActiveSlideFromScroll]);

    useEffect(() => {
        const element = scrollRef.current;

        if (!element || !isScrollMode || sections.length <= 1) {
            return undefined;
        }

        element.addEventListener('scroll', syncActiveSlideFromScroll, { passive: true });
        syncActiveSlideFromScroll();

        return () => {
            element.removeEventListener('scroll', syncActiveSlideFromScroll);
        };
    }, [sections.length, cardWidth, isScrollMode, syncActiveSlideFromScroll]);

    if (!sections.length) {
        return null;
    }

    return (
        <CarouselWrapper
            className={`hot-yoter-how-to-get-carousel-wrapper${showScrollArrows ? ' hot-yoter-how-to-get-carousel-wrapper_has-side-arrows' : ''}`}
            hasSideArrows={showScrollArrows}
            testId={`${testID}-wrapper`}
        >
            {showScrollArrows ? (
                <>
                    {/*
                      Hebrew RTL: start of the track is on the right.
                      Right arrow = הקודם (toward gold/start), left arrow = הבא.
                    */}
                    <CarouselArrow
                        type="button"
                        className={`hot-yoter-how-to-get-carousel__arrow hot-yoter-how-to-get-carousel__arrow_start${!canScrollTowardStart ? ' hot-yoter-how-to-get-carousel__arrow_disabled' : ''}`}
                        onClick={handleScrollTowardStart}
                        disabled={!canScrollTowardStart}
                        aria-label="הקודם"
                        testId={`${testID}-arrow-start`}
                    />
                    <CarouselArrow
                        type="button"
                        className={`hot-yoter-how-to-get-carousel__arrow hot-yoter-how-to-get-carousel__arrow_end${!canScrollTowardEnd ? ' hot-yoter-how-to-get-carousel__arrow_disabled' : ''}`}
                        onClick={handleScrollTowardEnd}
                        disabled={!canScrollTowardEnd}
                        aria-label="הבא"
                        testId={`${testID}-arrow-end`}
                    />
                </>
            ) : null}

            <CarouselRoot
                ref={wrapperRef}
                dir="rtl"
                allowVerticalOverflow={cardHeightOverride != null || fitsInline}
                className={`hot-yoter-how-to-get-carousel${fitsInline ? ' hot-yoter-how-to-get-carousel_fits-inline' : ' hot-yoter-how-to-get-carousel_scroll-mode'}${cardHeightOverride != null ? ' hot-yoter-how-to-get-carousel_has-tall-cards' : ''}`}
                testId={testID}
            >
                <CarouselScroll
                    ref={scrollRef}
                    dir={isScrollMode ? 'rtl' : 'ltr'}
                    className="hot-yoter-how-to-get-carousel__scroll"
                    fitsInline={fitsInline}
                    isDesktopInline={isDesktopInline}
                    isScrollMode={isScrollMode}
                    inlineGap={fitsInline ? inlineGap : undefined}
                    hasHeightOverride={cardHeightOverride != null}
                    cardRowHeight={isTabletUp && !isMobile ? cardRowHeight : null}
                    style={fitsInline ? { gap: `${inlineGap}px` } : undefined}
                    testId={`${testID}-scroll`}
                >
                    {carouselItems.map(({ section, index, key }) => (
                        <HotYoterHowToGetCard
                            key={key}
                            section={section}
                            index={index}
                            total={sections.length}
                            cardWidth={cardWidth}
                            cardMaxWidth={cardMaxWidth}
                            cardHeightOverride={cardHeightOverride}
                            fitsInline={fitsInline}
                            isMdUp={isMdUp}
                            hasResolvedContentHeight={hasResolvedContentHeight}
                            onBodyContentHeightReport={handleBodyContentHeightReport}
                            testID={testID}
                        />
                    ))}
                </CarouselScroll>

            </CarouselRoot>

                {showScrollDots ? (
                    <CarouselDots
                        className="hot-yoter-how-to-get-carousel__dots"
                        testId={`${testID}-dots`}
                        sx={{ direction: 'ltr' }}
                        aria-hidden={false}
                    >
                        {dotItems.map(({ section, index }) => (
                            <CarouselDot
                                key={`${section.title}-${index}-dot`}
                                type="button"
                                active={activeSlideIndex === index}
                                className={`hot-yoter-how-to-get-carousel__dot${activeSlideIndex === index ? ' hot-yoter-how-to-get-carousel__dot_active' : ''}`}
                                onClick={() => handleDotClick(index)}
                                aria-label={`שלב ${index + 1}`}
                                aria-current={activeSlideIndex === index ? 'true' : undefined}
                                testId={`${testID}-dot-${index}`}
                            />
                        ))}
                    </CarouselDots>
                ) : null}
        </CarouselWrapper>
    );
};
